/**
 * Speaker notes tab (presenters): slide aside.notes with TTS codewords as inline controls + font size.
 */

import { on, EVENTS } from '../core/events';
import type { LecturaHostAdapter, ChatRoleService, DrawerView } from '../core/types';
import { remoteTtsVerbose } from '../lib/remote-tts-log';
import {
  segmentNotesForSpeakerPanel,
  serializeQueueItemToCodeword,
  DEFAULT_TTS_FADEOUT_MS,
  type QueueItem,
  type SpeakerNoteSegment
} from '../services/tts-queue';
import { getRevealSpeakerNotesPlainTextFromSlide } from '../services/remote/v1/reveal-bridge';

const FONT_STORAGE_KEY = 'lectura-next-speaker-notes-font-px';
const FONT_MIN = 11;
const FONT_MAX = 26;
const FONT_DEFAULT = 14;

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMs(ms: number): string {
  if (ms >= 1000 && ms % 1000 === 0) return `${ms / 1000}s`;
  return `${ms}ms`;
}

function selLabel(sel: string | string[]): string {
  const s = Array.isArray(sel) ? sel.join(', ') : sel;
  const t = String(s || '').trim();
  if (t.length <= 28) return t;
  return t.slice(0, 25) + '…';
}

function codewordIsRunnable(item: QueueItem): boolean {
  if (item.type === 'pause' || item.type === 'speak') return false;
  return true;
}

function buildCodewordMarkup(
  seg: Extract<SpeakerNoteSegment, { type: 'codeword' }>,
  index: number | null
): string {
  const raw = escapeHtml(seg.rawInner.trim());
  const item = seg.item;
  if (!item) {
    return `<span class="rf-sn-cw rf-sn-cw--unknown" title="Unknown codeword">[[${raw}]]</span>`;
  }

  if (item.type === 'pause') {
    return (
      `<span class="rf-sn-cw rf-sn-cw--pause" title="Pause ${item.ms}ms (runs during TTS only)">` +
      `<span class="rf-sn-cw-ic" aria-hidden="true">⏸</span>` +
      `<span class="rf-sn-cw-meta">${escapeHtml(formatMs(item.ms))}</span>` +
      `</span>`
    );
  }

  if (item.type === 'speak' && item.text.includes('\u200B__TTS_NAME__')) {
    return (
      `<span class="rf-sn-cw rf-sn-cw--name" title="Student name (spoken during TTS)">` +
      `<span class="rf-sn-cw-ic" aria-hidden="true">👤</span>` +
      `<span class="rf-sn-cw-meta">name</span>` +
      `</span>`
    );
  }

  if (!codewordIsRunnable(item)) {
    return `<span class="rf-sn-cw rf-sn-cw--noop" title="[[${raw}]]">[[${raw}]]</span>`;
  }

  const title = escapeHtml(serializeQueueItemToCodeword(item) || `[${item.type}]`);
  const idxAttr = index !== null ? ` data-rf-sn-cw="${index}"` : '';

  switch (item.type) {
    case 'emoji':
      return `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--emoji"${idxAttr} title="Run ${title}">${escapeHtml(item.value)}</button>`;
    case 'click':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--click"${idxAttr} title="Click ${escapeHtml(selLabel(item.selector))}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🖱</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'focus':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--focus"${idxAttr} title="Focus ${escapeHtml(selLabel(item.selector))}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">◎</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'highlight':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--hl"${idxAttr} title="${item.ring ? 'Ring' : 'Highlight'} ${escapeHtml(selLabel(item.selector))}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">${item.ring ? '⭕' : '✨'}</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'starthighlight':
    case 'stophighlight':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--hl"${idxAttr} title="${item.type} ${escapeHtml(selLabel(item.selector))}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">${item.type === 'starthighlight' ? '▶' : '■'}</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'scroll':
    case 'waitclick':
    case 'play':
    case 'pausevideo':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="${item.type} ${escapeHtml(selLabel(item.selector))}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">⌖</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'chatdraft':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--type"${idxAttr} title="Open chat + draft">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">💬</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.text))}</code>` +
        `</button>`
      );
    case 'chatopen':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Open chat drawer">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">💬</span>` +
        `<span class="rf-sn-cw-meta">open chat</span>` +
        `</button>`
      );
    case 'chatcleardraft':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Clear chat draft">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🧹</span>` +
        `<span class="rf-sn-cw-meta">clear chat</span>` +
        `</button>`
      );
    case 'chatclose':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Close chat drawer">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">✖</span>` +
        `<span class="rf-sn-cw-meta">close chat</span>` +
        `</button>`
      );
    case 'zoom':
    case 'zoomin':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--zoom"${idxAttr} title="${title}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🔍</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'zoomout':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--zoom"${idxAttr} title="Zoom out">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🔍</span>` +
        `<span class="rf-sn-cw-meta">out</span>` +
        `</button>`
      );
    case 'keypress':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--key"${idxAttr} title="Key ${escapeHtml(item.combo)}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">⌨</span>` +
        `<kbd class="rf-sn-cw-kbd">${escapeHtml(item.combo)}</kbd>` +
        `</button>`
      );
    case 'type':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--type"${idxAttr} title="Type into ${escapeHtml(item.selector)}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">✎</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.selector))}</code>` +
        `</button>`
      );
    case 'sound': {
      const vol =
        item.volume !== undefined && item.volume < 1 - 1e-9 ? ` · vol ${item.volume}` : '';
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--sound"${idxAttr} title="Sound (wait) ${escapeHtml(item.key)}${vol}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🔊</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.key))}</code>` +
        `</button>`
      );
    }
    case 'soundasync': {
      const vol =
        item.volume !== undefined && item.volume < 1 - 1e-9 ? ` · vol ${item.volume}` : '';
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--soundasync"${idxAttr} title="Sound (async) ${escapeHtml(item.key)}${vol}">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🎵</span>` +
        `<code class="rf-sn-cw-sel">${escapeHtml(selLabel(item.key))}</code>` +
        `</button>`
      );
    }
    case 'stopsound':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--stopsound"${idxAttr} title="Stop sound">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">⏹</span>` +
        `<span class="rf-sn-cw-meta">stop</span>` +
        `</button>`
      );
    case 'fadeoutsound': {
      const ms = item.ms ?? DEFAULT_TTS_FADEOUT_MS;
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--fadeout"${idxAttr} title="Fade out (${ms}ms)">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">⏬</span>` +
        `<span class="rf-sn-cw-meta">${ms}ms</span>` +
        `</button>`
      );
    }
    case 'pausebreak':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--key"${idxAttr} title="Start break timer (${item.minutes} min)">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">☕</span>` +
        `<span class="rf-sn-cw-meta">${item.minutes}m</span>` +
        `</button>`
      );
    case 'pauseon':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Open pause screen">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">⏸</span>` +
        `<span class="rf-sn-cw-meta">pause on</span>` +
        `</button>`
      );
    case 'pauseoff':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Close pause screen">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">▶</span>` +
        `<span class="rf-sn-cw-meta">pause off</span>` +
        `</button>`
      );
    case 'qrshow':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Show remote QR">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">📱</span>` +
        `<span class="rf-sn-cw-meta">show QR</span>` +
        `</button>`
      );
    case 'qrhide':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Hide remote QR">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🙈</span>` +
        `<span class="rf-sn-cw-meta">hide QR</span>` +
        `</button>`
      );
    case 'qrbig':
      return (
        `<button type="button" class="rf-sn-cw rf-sn-cw--btn rf-sn-cw--act"${idxAttr} title="Open big QR">` +
        `<span class="rf-sn-cw-ic" aria-hidden="true">🔲</span>` +
        `<span class="rf-sn-cw-meta">big QR</span>` +
        `</button>`
      );
    default:
      return `<span class="rf-sn-cw rf-sn-cw--unknown" title="[[${raw}]]">[[${raw}]]</span>`;
  }
}

function renderSpeakerBody(rawNotes: string): { html: string; items: QueueItem[] } {
  const segments = segmentNotesForSpeakerPanel(rawNotes);
  const items: QueueItem[] = [];
  let html = '';
  for (const seg of segments) {
    if (seg.type === 'text') {
      html += escapeHtml(seg.text).replace(/\n/g, '<br>');
      continue;
    }
    const item = seg.item;
    let idx: number | null = null;
    if (item && codewordIsRunnable(item)) {
      items.push(item);
      idx = items.length - 1;
    }
    html += buildCodewordMarkup(seg, idx);
  }
  return { html, items };
}

export interface RegisterSpeakerNotesOpts {
  roleService?: ChatRoleService;
  /** Remote MQTT presenter: show tab even when Firestore chat role is not presenter. */
  remoteSessionPresenter?: boolean;
  /**
   * When true (remote shell with `sendRemoteTtsCodeword`), log codeword button taps if
   * `window.__LECTURA_DEBUG_REMOTE_TTS__` is set.
   */
  verboseRemoteTtsClicks?: boolean;
  /** Runs one TTS action (built-in TTS or RevealNotesTTS — orchestrator wires both). */
  runCodeword: (item: unknown) => Promise<void>;
}

export function registerSpeakerNotes(
  adapter: LecturaHostAdapter,
  drawerView: DrawerView,
  opts: RegisterSpeakerNotesOpts
): void {
  const bodyEl = drawerView.getSpeakerNotesBody?.();
  const tabBtn = drawerView.getTabSpeaker?.();
  if (!bodyEl || !tabBtn) return;

  const decBtn = drawerView.getSpeakerNotesFontDec?.();
  const incBtn = drawerView.getSpeakerNotesFontInc?.();

  function loadFontPx(): number {
    if (typeof window === 'undefined' || !window.localStorage) return FONT_DEFAULT;
    const n = parseInt(window.localStorage.getItem(FONT_STORAGE_KEY) || '', 10);
    if (!Number.isFinite(n)) return FONT_DEFAULT;
    return Math.min(FONT_MAX, Math.max(FONT_MIN, n));
  }

  function applyFont(px: number): void {
    bodyEl.style.setProperty('--rf-sn-font', `${px}px`);
    try {
      window.localStorage.setItem(FONT_STORAGE_KEY, String(px));
    } catch {
      /* ignore */
    }
  }

  applyFont(loadFontPx());

  decBtn?.addEventListener('click', () => {
    applyFont(Math.max(FONT_MIN, loadFontPx() - 1));
  });
  incBtn?.addEventListener('click', () => {
    applyFont(Math.min(FONT_MAX, loadFontPx() + 1));
  });

  function presenterVisible(): boolean {
    if (opts.remoteSessionPresenter) return true;
    const r = opts.roleService?.getRole?.();
    return r === 'presenter' || r === 'admin';
  }

  function setTabVisible(visible: boolean): void {
    tabBtn.classList.toggle('rf-hidden', !visible);
    if (!visible && drawerView.getActiveTab?.() === 'speaker') {
      drawerView.setTab?.('chat');
    }
  }

  function getNotesRaw(): string {
    if (typeof adapter.getSpeakerNotesPlainText === 'function') {
      return String(adapter.getSpeakerNotesPlainText()).trim();
    }
    const slide = adapter.getCurrentSlide?.() ?? null;
    return getRevealSpeakerNotesPlainTextFromSlide(slide);
  }

  let appliedRemoteInitialSpeakerTab = false;

  function refresh(): void {
    if (!presenterVisible()) {
      setTabVisible(false);
      return;
    }
    setTabVisible(true);
    // Remote MQTT presenter: mountDrawer runs before this tab is un-hidden, so initialTab 'speaker' was downgraded to 'chat'.
    if (opts.remoteSessionPresenter && !appliedRemoteInitialSpeakerTab) {
      appliedRemoteInitialSpeakerTab = true;
      drawerView.setTab?.('speaker');
    }
    const raw = getNotesRaw();
    if (!raw) {
      bodyEl.innerHTML =
        '<p class="rf-sn-empty">No speaker notes on this slide.</p>';
      (bodyEl as HTMLElement & { __rfSnItems?: QueueItem[] }).__rfSnItems = [];
      return;
    }
    const { html, items } = renderSpeakerBody(raw);
    bodyEl.innerHTML = html;
    (bodyEl as HTMLElement & { __rfSnItems?: QueueItem[] }).__rfSnItems = items;
  }

  bodyEl.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement | null;
    const btn = t?.closest?.('button[data-rf-sn-cw]') as HTMLButtonElement | null;
    if (!btn) return;
    ev.preventDefault();
    const idx = parseInt(btn.getAttribute('data-rf-sn-cw') || '', 10);
    const list = (bodyEl as HTMLElement & { __rfSnItems?: QueueItem[] }).__rfSnItems;
    const item = list && Number.isFinite(idx) ? list[idx] : undefined;
    if (!item) return;
    if (opts.verboseRemoteTtsClicks) {
      remoteTtsVerbose('speaker-notes codeword button clicked', { type: (item as QueueItem).type });
    }
    void opts.runCodeword(item).catch(() => {});
  });

  on(EVENTS.SLIDE_CHANGED, refresh);
  on(EVENTS.USER_CHANGED, refresh);
  adapter.subscribeSlideChange(refresh);
  opts.roleService?.subscribe?.(refresh);
  refresh();
}
